import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../state/store";
import { getAllDonations } from "../../services/DonationService";
import { FaFilter, FaPlus } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";

// Components
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { PageLayout } from '../../components/layout';
import { TableWithSelection } from '../../components/Table/TableWithSelection';
import { TableColumn } from '../../components/Table/Table.types';
import { SearchInput } from '../../components/Search';
import DonationFiltersModal from "../../components/Donation/DonationFiltersModal";
import DonationAssignModal from "../../components/Donation/DonationAssignModal";
import AddDonationModal from "../../components/Donation/AddDonationModal";
import DonationDetailsSideNav from "../../components/Donation/DonationDetailsSideNav";
import Pagination from "../../components/Pagination";

// Utilities
import { formatDonationAmount } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateFormatter';
import { ShipmentDonation } from "../../types/shipment.types";
import { debounce } from '../../utils/debounce';

const PAGE_SIZE = 20;

// Types
interface DonationDetails {
    donorName: string;
    donorEmail: string;
    donationValue: number;
    donationCurrency: string;
    donationID: string;
    donationType: string;
    appType: string;
    donationFrequency: string;
    campaign: string;
    localPartner: string;
    shipment: string;
    assignedStatus: string;
    frequancey: string;
    items: Array<{ itemName: string; unitCost: number; quantity: number }>;
    createAt: string;
}

/**
 * Donations page component using the standardized TableWithSelection component
 */
const DonationPageStandardized: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    // Redux state
    const donations = useSelector((state: RootState) => state.donation.donationList);

    // Local data state
    const [activeFilters, setActiveFilters] = useState<any>({});

    // UI state
    const [showFilters, setShowFilters] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showAddDonationModal, setShowAddDonationModal] = useState(false);
    const [showDonationDetails, setShowDonationDetails] = useState(false);
    const [selectedDonationDetails, setSelectedDonationDetails] = useState<DonationDetails | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [resetPagination, setResetPagination] = useState(false);
    
    // Selection state
    const [selectedDonations, setSelectedDonations] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<any>>(new Set());
    const [allPageSelected, setAllPageSelected] = useState(false);

    // Search state
    const [inputValue, setInputValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    


    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            setSearchTerm(value);
        }, 400),
        []
    );

    const handleInputChange = (value: string) => {
        setInputValue(value);
        debouncedSearch(value);
    };

    // Fetch donations via Redux
    useEffect(() => {
        dispatch(getAllDonations(activeFilters));
    }, [dispatch, activeFilters]);

    // CSV export functionality
    function exportToCSV(data: any[], filename = "donations.csv") {
        if (data.length === 0) {
            alert("No data to export");
            return;
        }

        // Format data for CSV
        const header = [
            "Donation Date", "Campaign", "Donor", 
            "Donation Amount", "Currency", "No. of Bars", 
            "Shipment Name", "Status"
        ];
        
        // Convert data to CSV rows
        const rows = data.map(d => [
            d.donationDate ? formatDate(d.donationDate) : "",
            d.campaign?.name || "",
            d?.dontation?.name || "",
            d.amount || "",
            d.currency || "",
            d.numberOfUnits || "",
            d?.ShipmentsOnDonations?.map((item: ShipmentDonation) => item.shipment.name).join(',') || "-",
            d.assignedStatus || "Unassigned"
        ]);
        
        // Combine header and rows
        const csvContent = [
            header.join(","),
            ...rows.map(row => row.join(","))
        ].join("\n");
        
        // Create and download link
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    // Handle modal interactions
    const handleNewDonationModalClose = () => {
        setShowAddDonationModal(false);
        dispatch(getAllDonations(activeFilters));
    };

    const handleAssignModalClose = () => {
        setShowAssignModal(false);
        dispatch(getAllDonations(activeFilters));
    };

    // Handle row click to show details
    const handleRowClick = (donation: any) => {
        // Map donation data to the format expected by DonationDetailsSideNav
        const donationDetails = {
            donorName: donation?.dontation?.name || '',
            donorEmail: donation?.dontation?.email || '',
            donationValue: donation?.amount || 0,
            donationCurrency: donation?.currency || '',
            donationID: donation?.id || donation?._id || '',
            donationType: donation?.type || donation?.donationType || '',
            appType: donation?.appType || '',
            donationFrequency: donation?.frequency || 'One-time',
            campaign: donation?.campaign?.name || '',
            localPartner: donation?.localPartner || '',
            shipment: donation?.ShipmentsOnDonations?.map((s: any) => s.shipment.name).join(', ') || '',
            assignedStatus: donation?.assignedStatus || 'Unassigned',
            items: donation?.items || [],
            frequancey: donation?.frequency || 'One-time',
            createAt: donation?.donationDate || new Date().toISOString()
        };
        
        setSelectedDonationDetails(donationDetails);
        setShowDonationDetails(true);
    };

    // Handle donation selection
    const handleSelectDonation = (donation: any, checked: boolean) => {
        const id = donation.id ?? donation._id ?? donation.donationDate + donation?.dontation?.name;
        
        setSelectedDonations(prev => {
            if (checked) {
                return [...prev, donation];
            } else {
                return prev.filter(d => {
                    const dId = d.id ?? d._id ?? d.donationDate + d?.dontation?.name;
                    return dId !== id;
                });
            }
        });
        
        setSelectedIds(prev => {
            const newSelectedIds = new Set(prev);
            if (checked) {
                newSelectedIds.add(id);
            } else {
                newSelectedIds.delete(id);
            }
            return newSelectedIds;
        });
        
        // Update allPageSelected if needed
        if (!checked && allPageSelected) {
            setAllPageSelected(false);
        } else if (checked) {
            // Check if all page items are now selected
            const pageIds = paginatedDonations.map(d => 
                d.id ?? d._id ?? d.donationDate + d?.dontation?.name
            );
            
            const allSelected = pageIds.every(id => {
                if (id === (donation.id ?? donation._id ?? donation.donationDate + donation?.dontation?.name)) {
                    return checked;
                }
                return selectedIds.has(id);
            });
            
            setAllPageSelected(allSelected);
        }
    };

    // Handle "select all" on current page
    const handleSelectAll = (checked: boolean) => {
        setAllPageSelected(checked);
        
        const pageIds = paginatedDonations.map(d => 
            d.id ?? d._id ?? d.donationDate + d?.dontation?.name
        );
        
        setSelectedIds(prev => {
            const newSelectedIds = new Set(prev);
            
            if (checked) {
                pageIds.forEach(id => newSelectedIds.add(id));
            } else {
                pageIds.forEach(id => newSelectedIds.delete(id));
            }
            
            return newSelectedIds;
        });
        
        setSelectedDonations(prev => {
            if (checked) {
                // Add all page donations not already in the selection
                const currentIds = new Set(prev.map(d => 
                    d.id ?? d._id ?? d.donationDate + d?.dontation?.name
                ));
                
                const newItems = paginatedDonations.filter(d => {
                    const id = d.id ?? d._id ?? d.donationDate + d?.dontation?.name;
                    return !currentIds.has(id);
                });
                
                return [...prev, ...newItems];
            } else {
                // Remove all page donations
                return prev.filter(d => {
                    const id = d.id ?? d._id ?? d.donationDate + d?.dontation?.name;
                    return !pageIds.includes(id);
                });
            }
        });
    };

    // Filter donations based on search term and active filters (from full list, never paginated)
    const filteredDonations = useMemo(() => {
        let result = [...donations];

        // Apply search term filter
        if (searchTerm) {
            result = result.filter(donation =>
                (donation.campaign?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (donation?.dontation?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (donation.assignedStatus || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply activeFilters (client-side filtering)
        if (activeFilters && Object.keys(activeFilters).length > 0) {
            // Filter by campaign if specified
            if (activeFilters.campaignIds && activeFilters.campaignIds.length > 0) {
                result = result.filter(donation =>
                    donation.campaign && activeFilters.campaignIds.includes(donation.campaign.id)
                );
            }

            // Filter by donor if specified
            if (activeFilters.donorIds && activeFilters.donorIds.length > 0) {
                result = result.filter(donation =>
                    donation.dontation && activeFilters.donorIds.includes(donation.dontation.id)
                );
            }

            // Filter by shipment if specified
            if (activeFilters.shipmentIds && activeFilters.shipmentIds.length > 0) {
                result = result.filter(donation =>
                    donation.ShipmentsOnDonations && donation.ShipmentsOnDonations.some((s: any) =>
                        activeFilters.shipmentIds.includes(s.shipment.id)
                    )
                );
            }

            // Filter by status if specified
            if (activeFilters.statuses && activeFilters.statuses.length > 0) {
                result = result.filter(donation =>
                    activeFilters.statuses.includes(donation.assignedStatus)
                );
            }

            // Filter by date range if specified
            if (activeFilters.fromDate) {
                const fromDate = new Date(activeFilters.fromDate);
                result = result.filter(donation => {
                    const donationDate = donation.donationDate ? new Date(donation.donationDate) : null;
                    return donationDate ? donationDate >= fromDate : false;
                });
            }

            if (activeFilters.toDate) {
                const toDate = new Date(activeFilters.toDate);
                result = result.filter(donation => {
                    const donationDate = donation.donationDate ? new Date(donation.donationDate) : null;
                    return donationDate ? donationDate <= toDate : false;
                });
            }
        }

        return result;
    }, [donations, searchTerm, activeFilters]);

    // Paginate from filtered results
    const paginatedDonations = useMemo(
        () => filteredDonations.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [filteredDonations, currentPage]
    );

    // Reset pagination when filtered results change
    useEffect(() => {
        setCurrentPage(1);
        setResetPagination((v) => !v);
    }, [filteredDonations]);

    // Define table columns
    const getColumns = (): TableColumn[] => [
        {
            id: 'donationDate',
            label: 'Created at',
            priority: 'high',
            format: (value, row) => row.donationDate ? formatDate(row.donationDate) : ""
        },
        {
            id: 'campaign',
            label: 'Campaign',
            priority: 'high',
            format: (value, row) => row.campaign?.name || ""
        },
        {
            id: 'donor',
            label: 'Donor',
            priority: 'medium',
            format: (value, row) => row?.dontation?.name || ""
        },
        {
            id: 'amount',
            label: 'Donation amount',
            priority: 'medium',
            format: (value, row) => formatDonationAmount(row.amount, row.exchangeAmount, row.currency)
        },
        {
            id: 'numberOfUnits',
            label: 'No. of bars',
            priority: 'medium'
        },
        {
            id: 'shipment',
            label: 'Shipment name',
            priority: 'low',
            format: (value, row) => 
                row?.ShipmentsOnDonations?.map((item: ShipmentDonation) => item.shipment.name).join(',') || "-"
        },
        {
            id: 'source',
            label: 'Source',
            priority: 'medium',
            format: (value, row) => row?.appType ? (row.appType === 'OWNER_REWARD_REDEMPTION' ? 'Owner reward redeemed' : row.appType.replace(/_/g, ' ')) : ''
        },
        {
            id: 'status',
            label: 'Donation status',
            priority: 'high',
            format: (value, row) => (
                <Badge
                    label={row.assignedStatus || "Unassigned"}
                    variant={row.assignedStatus === "ASSIGNED" ? "success" : "warning"}
                    size="md"
                />
            )
        }
    ];
    
    // Generate a consistent key for each donation
    const getDonationKey = (donation: any) =>
        donation.id ?? donation._id ?? donation.donationDate + donation?.dontation?.name;

    // Render action buttons for PageLayout
    const renderActions = () => (
        <div className="flex gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        startIcon={<FaFilter />} 
                        onClick={() => setShowFilters(true)}
                    >
                        Filters
                    </Button>
                    <Button
                        variant="primary" 
                        size="sm"
                        startIcon={<FaPlus />} 
                        onClick={() => setShowAddDonationModal(true)}
                    >
                        Add Donation
                    </Button>
                    <Button 
                        variant="secondary"
                        size="sm"
                        startIcon={<FiDownload />}
                        disabled={selectedDonations.length === 0}
                        onClick={() => exportToCSV(selectedDonations, "selected_donations.csv")}
                    >
                        Export Selected
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={selectedDonations.length === 0}
                        onClick={() => setShowAssignModal(true)}
                    >
                        Assign to Shipment
                    </Button>
        </div>
    );

    // Render search box for PageLayout
    const renderSearchBox = () => (
        <SearchInput
            value={inputValue}
            placeholder="Search donations..."
            onChange={handleInputChange}
            showClearButton={true}
            debounce={0} // Disable component's internal debouncing
        />
    );

    // Render pagination controls for PageLayout
    const renderPagination = () =>
        filteredDonations.length > PAGE_SIZE ? (
            <Pagination
                totalRecords={filteredDonations.length}
                perPageCount={PAGE_SIZE}
                reset={resetPagination}
                setPerPageDataSet={(start, _end) => {
                    const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE));
                    setCurrentPage(nextPage);
                }}
            />
        ) : null;

    return (
        <>
            <PageLayout
                title="Donations"
                actions={renderActions()}
                filters={renderSearchBox()}
                footer={renderPagination()}
            >
                <div className="overflow-x-auto max-h-[calc(100vh-280px)]">
                    <TableWithSelection
                        data={paginatedDonations}
                        columns={getColumns()}
                        selectedIds={selectedIds}
                        onSelectionChange={handleSelectDonation}
                        onSelectAll={handleSelectAll}
                        allSelected={allPageSelected}
                        onRowClick={handleRowClick}
                        hoverable={true}
                        getRowKey={getDonationKey}
                        emptyMessage="No donations found matching your criteria"
                        fixedHeader={true}
                    />
                </div>
            </PageLayout>

            {/* Modals - moved outside PageLayout */}
            <DonationFiltersModal
                open={showFilters}
                onClose={() => setShowFilters(false)}
                onApply={(donations, filters) => {
                    setActiveFilters(filters);
                    setCurrentPage(1);
                    setResetPagination((v) => !v);
                }}
            />

            <DonationAssignModal
                open={showAssignModal}
                onClose={handleAssignModalClose}
                donations={selectedDonations}
            />

            <AddDonationModal
                open={showAddDonationModal}
                onClose={handleNewDonationModalClose}
            />

            <DonationDetailsSideNav
                isOpen={showDonationDetails}
                onClose={() => setShowDonationDetails(false)}
                donationDetails={selectedDonationDetails}
            />
        </>
    );
};

export default DonationPageStandardized;